import {Pagination} from "../filters/pagination";
import type { ProgrammingTripClosure } from '../utils/programming-trip-closure';

export enum EProgrammingTravelType {
    Empty = 0,
    Loaded = 1
}

export enum EProgrammingStatus {
    Waiting = 0,           // Aguardando início
    Created = 1,           // Criada
    Loading = 2,           // Em carregamento
    LoadedTransit = 3,     // Em trânsito carregado
    Unloading = 4,         // Em descarga
    Completed = 5,         // Concluída
    Finished = 6,          // Finalizada (após conclusão)
    Cancelled = 7,         // Cancelada
    InProgress = 8,        // Em progresso
    Available = 9,         // Disponível
    EmptyTransit = 10,     // Em trânsito vazio
    DelayedDeparture = 11, // Atraso na saída
    DelayedLoading = 12,   // Atraso no carregamento
    DelayedTransit = 13,   // Atraso em trânsito
    DelayedUnloading = 14, // Atraso na descarga
    DelayedArrival = 15    // Atraso na chegada
}

/** Textos alinhados a STATUS_LABELS (glossário Torre / planilha TNE). */
export const statusOptions = [
    { key: EProgrammingStatus.Waiting, text: 'Aguardando início', value: EProgrammingStatus.Waiting },
    { key: EProgrammingStatus.Created, text: 'Criada', value: EProgrammingStatus.Created },
    { key: EProgrammingStatus.Loading, text: 'Em carregamento', value: EProgrammingStatus.Loading },
    { key: EProgrammingStatus.LoadedTransit, text: 'Em trânsito carregado', value: EProgrammingStatus.LoadedTransit },
    { key: EProgrammingStatus.Unloading, text: 'Em descarga', value: EProgrammingStatus.Unloading },
    { key: EProgrammingStatus.Completed, text: 'Concluída', value: EProgrammingStatus.Completed },
    { key: EProgrammingStatus.Finished, text: 'Finalizada', value: EProgrammingStatus.Finished },
    { key: EProgrammingStatus.Cancelled, text: 'Cancelada', value: EProgrammingStatus.Cancelled },
    { key: EProgrammingStatus.InProgress, text: 'Em progresso', value: EProgrammingStatus.InProgress },
    { key: EProgrammingStatus.Available, text: 'Disponível', value: EProgrammingStatus.Available },
    { key: EProgrammingStatus.EmptyTransit, text: 'Em trânsito vazio', value: EProgrammingStatus.EmptyTransit },
    { key: EProgrammingStatus.DelayedDeparture, text: 'Atraso na saída', value: EProgrammingStatus.DelayedDeparture },
    { key: EProgrammingStatus.DelayedLoading, text: 'Atraso no carregamento', value: EProgrammingStatus.DelayedLoading },
    { key: EProgrammingStatus.DelayedTransit, text: 'Atraso em trânsito', value: EProgrammingStatus.DelayedTransit },
    { key: EProgrammingStatus.DelayedUnloading, text: 'Atraso na descarga', value: EProgrammingStatus.DelayedUnloading },
    { key: EProgrammingStatus.DelayedArrival, text: 'Atraso na chegada', value: EProgrammingStatus.DelayedArrival }
];

export const travelTypeOptions = [
    { key: EProgrammingTravelType.Empty, text: 'Vazio', value: EProgrammingTravelType.Empty },
    { key: EProgrammingTravelType.Loaded, text: 'Carregado', value: EProgrammingTravelType.Loaded }
];

export interface Programming {
    id: string;
    createdAt: string;
    updatedAt?: string;
    createdBy?: string;
    createdByUserId?: string;
    canEdit?: boolean;

    // Cliente
    clientId: string;
    clientName: string;

    // Placas
    mainVehiclePlateId?: string;
    mainVehiclePlateName?: string;

    semiTrailer1Id?: string;
    semiTrailer1Name?: string;

    semiTrailer2Id?: string;
    semiTrailer2Name?: string;

    // Volume
    volume: number;

    // Produto
    productId: string;
    productName: string;

    // Motorista
    driverId?: string;
    driverName?: string;

    // Tipo de Deslocamento
    travelType: EProgrammingTravelType | string;

    // Rota
    routeId?: string;
    routeName?: string;

    // Referências
    originReferenceId: string;
    originReferenceName: string;

    destinationReferenceId: string;
    destinationReferenceName: string;

    returnDestinationReferenceId?: string;
    returnDestinationReferenceName?: string;

    isReturn: boolean;
    isGoAndReturn?: boolean;

    // Datas e Horários (legado + por etapa)
    expectedDepartureTime?: string;
    expectedArrivalTime?: string;
    expectedOriginDepartureAt?: string;
    expectedDestinationArrivalAt?: string;
    expectedDestinationDepartureAt?: string;
    expectedReturnArrivalAt?: string;
    actualOriginEnteredAt?: string;
    actualOriginExitedAt?: string;
    actualDestinationEnteredAt?: string;
    actualDestinationExitedAt?: string;
    actualReturnEnteredAt?: string;
    actualReturnExitedAt?: string;

    // Distância
    distanceKm?: number;

    // Status
    status: EProgrammingStatus | string;

    // Cancelamento
    cancellationReason?: string;
    cancelledBy?: string;
    cancelledAt?: string;

    // Localização atual (novos campos)
    currentLatitude?: number;
    currentLongitude?: number;
    currentAddressInfo?: string;
    lastLocationUpdate?: string;

    isActive: boolean;

    tripClosure?: ProgrammingTripClosure;
}

export interface CreateProgrammingRequest {
    clientId: string;
    mainVehiclePlateId?: string;
    semiTrailer1Id?: string;
    semiTrailer2Id?: string;
    volume: number;
    productId: string;
    driverId?: string;
    travelType: EProgrammingTravelType | string;
    routeId?: string;
    originReferenceId: string;
    destinationReferenceId: string;
    returnDestinationReferenceId?: string;
    expectedDepartureTime?: string;
    expectedArrivalTime?: string;
    expectedOriginDepartureAt?: string;
    expectedDestinationArrivalAt?: string;
    expectedDestinationDepartureAt?: string;
    expectedReturnArrivalAt?: string;
    distanceKm?: number;
    isReturn?: boolean | false;
    isGoAndReturn?: boolean;
}

export interface UpdateProgrammingRequest {
    clientId?: string;
    mainVehiclePlateId?: string;
    semiTrailer1Id?: string;
    semiTrailer2Id?: string;
    volume?: number;
    productId?: string;
    driverId?: string;
    travelType?: EProgrammingTravelType | string;
    routeId?: string;
    originReferenceId?: string;
    destinationReferenceId?: string;
    returnDestinationReferenceId?: string;
    expectedDepartureTime?: string;
    expectedArrivalTime?: string;
    expectedOriginDepartureAt?: string;
    expectedDestinationArrivalAt?: string;
    expectedDestinationDepartureAt?: string;
    expectedReturnArrivalAt?: string;
    distanceKm?: number;
    isReturn?: boolean;
    isGoAndReturn?: boolean;
    status?: EProgrammingStatus | string;
    auditSource?: string;
}

export interface ProgrammingAuditLogEntry {
    id: string;
    programmingId: string;
    userId?: string;
    userDisplayName: string;
    userRole: string;
    changedAtUtc: string;
    source: string;
    fieldName: string;
    oldValue?: string;
    newValue?: string;
}

export interface CancelProgrammingRequest {
    reason: string;
}

export interface ProgrammingFilter extends Pagination {
    clientId?: string;
    mainVehiclePlateId?: string;
    semiTrailer1Id?: string;
    semiTrailer2Id?: string;
    productId?: string;
    driverId?: string;
    travelType?: EProgrammingTravelType;
    routeId?: string;
    originReferenceId?: string;
    destinationReferenceId?: string;
    status?: EProgrammingStatus;
    startDate?: string;
    endDate?: string;
    page?: number;
    perPage?: number;
    sortField?: string;
    sortType?: string;
    plate?: string;
    onlyWithLocation?: boolean;
    hasCurrentLocation?: boolean;
    lastLocationAfter?: string;
    lastLocationBefore?: string;
    orderBy?: string;
    torreWindowMode?: 'hoje' | 'periodo';
    torreTerminalLookbackDays?: number;
    includeTorreCancelled?: boolean;
}

export interface ProgrammingTorreSummary {
    aguardando: number;
    emExecucao: number;
    atrasadas: number;
    finalizadas: number;
    canceladas: number;
    total?: number;
}